import { ConfigService } from '@app/finxone-web-frontend/app/lib/services/config-service/config-service.service';
import { CtaButtonSignalService } from '@app/finxone-web-frontend/app/lib/services/cta-button-signal-service/cta-button-signal.service';
import { WorkflowFormsService } from '@app/finxone-web-frontend/app/lib/services/workflow-forms/workflow-forms.service';
import { FormActionState } from '@app/finxone-web-frontend/app/lib/state/form-submision.state';
import { ProfileState } from '@app/finxone-web-frontend/app/lib/state/user-profile.state';
import { FormID } from '@finxone-platform/form-action';
import {
  ButtonActionType,
  ProfileStateModel,
  UiZoneWidgetAttributeConfig,
} from '@finxone-platform/shared/sys-config-types';
import { Store } from '@ngxs/store';
import { parsePhoneNumber } from 'libphonenumber-js';
import { debounceTime, Observable } from 'rxjs';
import { flattenObject } from '../../../utils';
import { booleanKeysToArray } from '../../../workflow-forms-utils/user-profile/user-profile-update';

export const updateUserProfileCtaValidationMap: Map<
  ButtonActionType,
  (
    store: Store,
    ctaButtonSignalService: CtaButtonSignalService,
    attri?: UiZoneWidgetAttributeConfig,
    _?: ConfigService,
    workflowFormsService?: WorkflowFormsService,
  ) => Observable<boolean>
> = new Map<ButtonActionType, any>([
  [
    ButtonActionType.SUBMIT_USER_PROFILE_UPDATE,
    (
      store: Store,
      ctaButtonSignalService: CtaButtonSignalService,
      attri: UiZoneWidgetAttributeConfig,
      __?: ConfigService,
      workflowFormsService?: WorkflowFormsService,
    ) => {
      const key: string = attri?.['buttonActionType'] as string;
      const data = store.select(FormActionState.getFormActionStateWithId(FormID.UPDATE_USER_PROFILE));
      const profileData = store.selectSnapshot(ProfileState.getProfile);
      const profilePayload = getProfileData(profileData);

      // Initially disable the CTA button until the validation logic runs and determines if it should be enabled or not.
      ctaButtonSignalService.setSignal({ [key]: false });

      return data.pipe(debounceTime(300)).subscribe({
        next: (res) => {
          const formData = res?.formData;

          // Determine if the CTA Action should be enabled:
          // - Check if any profile data has changed (`hasChanged`).
          // - If email exists, it must be verified (`isEmailVerified`).
          // - If phone exists, it must be verified (`isPhoneVerified`).
          // - Enable CTA only if data changed and all present contact fields are verified.

          const hasChanged = hasProfileChanged(formData, profilePayload);
          const isEmailValid = !formData?.email || formData?.isEmailVerified === true;
          const isPhoneValid = !formData?.phoneNumber || formData?.isPhoneVerified === true;

          const shouldShowCta = hasChanged && isEmailValid && isPhoneValid;

          ctaButtonSignalService.setSignal({
            [key]: shouldShowCta,
          });
        },
      });
    },
  ],
]);

function getProfileData(profileData: ProfileStateModel): Record<string, any> | undefined {
  if (!profileData) return;
  const parsedPhone = parsePhoneNumber(profileData?.phoneNumber || '');
  const flattenProfileDataExtraAttributes = profileData?.extraAttributes || {};

  const profilePayload = {
    ...profileData,
    ...flattenProfileDataExtraAttributes,
    marketingPreferences: booleanKeysToArray(profileData?.marketingPreferences ?? {}),
    countryCode: parsedPhone?.countryCallingCode ? `+${parsedPhone.countryCallingCode}` : undefined,
    phoneNumber: parsedPhone?.nationalNumber,
    email: profileData.email,
    verifiedEmail: profileData.email,
    verifiedCountryCode: parsedPhone?.countryCallingCode ? `+${parsedPhone.countryCallingCode}` : undefined,
    verifiedPhone: parsedPhone?.nationalNumber,
    isEmailVerified: profileData.emailVerified,
    isPhoneVerified: profileData.phoneVerified,
    dob: profileData.dob,
  } as any;
  return profilePayload;
}

const IGNORED_PROFILE_KEYS = new Set(['emailVerifiedAt', 'phoneVerifiedAt', 'isModal']);

function hasProfileChanged(
  formData: Record<string, any> | undefined,
  profilePayload: Record<string, any> | undefined,
): boolean {
  if (!formData) return false;

  return Object.keys(formData).some((key) => {
    if (IGNORED_PROFILE_KEYS.has(key)) return false;

    const formValue = normalizeValue(key, formData?.[key]);
    const profileValue = normalizeValue(key, profilePayload?.[key]);

    return formValue !== profileValue;
  });
}

function normalizeValue(key: string, value: any): string {
  if (value === null || value === undefined) return '';
  if (key === 'dob') {
    const date = new Date(value);
    return isNaN(date.getTime()) ? '' : date.toISOString().split('T')[0];
  }

  // eg: ['email,'sms']
  if (Array.isArray(value)) {
    return value.slice().sort().join('|'); // sort to ignore order
  }

  return String(value).trim();
}
